// static data
import { SPRITESHEET_UI, TWEEN_ONCOMPLETE } from "Data/Constants";

// interfaces and types
import { GameConfig, ItemID } from "Data/GameConfig";
import type { IHiddenItemConfig } from "Interfaces/IHiddenItemConfig";
import type { IHidingSpotConfig } from "Interfaces/IHidingSpotConfig";
import type { IHidingSpotState } from "Interfaces/IHidingSpotState";
import type { IItemState } from "Interfaces/IItemState";
import type { ILevelState } from "Interfaces/ILevelState";

// gameobjects
import { Button } from "GameObjects/Button";
import { HiddenItem } from "GameObjects/GameExterior/HiddenItem";
import { TimelineLite } from "gsap/TimelineLite";
import { TweenLite } from "gsap/TweenLite";
import { makeSprite } from "html-living-framework/lib/Utils/PixiUtils";

// los singletones
import { Application } from "Application";
import { GameState } from "Models/GameState";

// pubsub topics
import * as Topics from "Data/Topics";
import { IFocusable, PixelPerfectHitArea } from "html-living-framework/lib/Input";

export class HidingSpot extends Button implements IFocusable {
    public get searched() { return this._searched; }
    public get hasItem() { return this._hasItem; }
    public get item() { return this._item; }

    protected backgroundContainer = this.addChild(new PIXI.Container());
    protected foregroundContainer = this.addChild(new PIXI.Container());
    protected foregroundSprites: PIXI.Sprite[] = [];
    protected backgroundSprites: PIXI.Sprite[] = [];
    protected redx: PIXI.Sprite;

    protected id: string | number;
    protected _searched: boolean;
    protected _itemID: ItemID | undefined;
    protected _hasItem: boolean;
    protected _item: HiddenItem | undefined;
    protected _focusSize: PIXI.Point | undefined;
    protected searchAnimation?: TimelineLite;
    private readonly _subscriptions: any[] = [];

    constructor(
        pCallback: () => void,
        config: IHidingSpotConfig,
        state: IHidingSpotState,
        levelState: ILevelState,
    ) {
        super(pCallback, "empty-pixel", SPRITESHEET_UI);
        this._image!.visible = false; // empty-pixel
        this.id = state.id;
        this.name = this.id;

        this._itemID = state.itemID;
        this._hasItem = !!state.itemID;
        this._searched = state.searched;

        this.SetPosition(config, state);
        this.SetSprites(levelState, config);
        this.SetHiddenItem(state, levelState);
        this.SetHitArea(config, state, levelState);
        if (config.hitarea.width && config.hitarea.height) {
            this._focusSize = new PIXI.Point(config.hitarea.width, config.hitarea.height);
        }

        this.setChildIndex(this.backgroundContainer, 0);
        this.setChildIndex(this.foregroundContainer, this.children.length - 1);

        this.redx = this.addChild(makeSprite("redx", SPRITESHEET_UI));
        this.redx.visible = false;

/*
        const dot = new PIXI.Graphics();
        dot.beginFill(0xff0000);
        dot.drawCircle(0, 0, 2);
        dot.endFill();
        this.addChild(dot);
 */
        this._subscriptions.push(
            PubSub.subscribe(Topics.HIDING_SPOT_SEARCHED, this.onMessageReceived.bind(this)),
            PubSub.subscribe(Topics.ITEM_FOUND, this.onMessageReceived.bind(this)),
        );
    }

    public isFocusable() {
        const globalPos = this.getFocusPosition();
        const stageWidth = Application.instance.resizer.getSize().x * Application.instance.resizer.getStageScale();
        return globalPos.x > 0 && globalPos.x < stageWidth;
    }

    public getFocusSize() {
        if (this._focusSize !== undefined) {
            return this._focusSize;
        }
        else {
            return new PIXI.Point(150, 150);
        }
    }

    public getFocusPosition(): PIXI.Point {
        if (this.item === undefined || this._focusSize !== undefined) {
            return this.getGlobalPosition();
        }
        else {
            return this.toGlobal(this.item.offset);
        }
    }

    /** @override */
    public onFocusBegin() {
        this.interactive = true; // for keyboard only
        super.onFocusBegin();
    }

    /** @override */
    public onFocusEnd() {
        this.interactive = false; // clicks must be on the hidden item itself not on the hiding spot
    }

    public onFocusActivated(): void {
        super.onFocusActivated();
        // PubSub.publishSync(CLEAR_FOCUS, this);
    }

    public destroy(pOptions ?: PIXI.DestroyOptions | boolean) {
        this._subscriptions.forEach(PubSub.unsubscribe);
        TweenLite.killTweensOf(this);
        if (this.foregroundSprites !== undefined) {
            // @ts-ignore
            this.foregroundSprites.hitArea = null;
        }
        if (this.backgroundSprites !== undefined) {
            // @ts-ignore
            this.backgroundSprites.hitArea = null;
        }
        super.destroy(pOptions);
    }

    protected DoSearchAnimation() {
        const delay = (this.item && this.item.photoMode && !this.item.found) ? 2 : 0;

        this.searchAnimation?.kill();
        this.searchAnimation = new TimelineLite();

        if (this.item === undefined || this.item.found) {
            if (this.item) {
                this.redx.position.copy(this.item.offset);
            }
            this.redx.visible = true;
            this.searchAnimation.fromTo(this.redx, 0.3,
                {pixi: {
                    scale: 1,
                    alpha: 1,
                }},
                {pixi: {
                    scale: 0,
                    alpha: 0,
                }})
                .delay(0.5)
                .eventCallback(TWEEN_ONCOMPLETE, () => {
                    this.redx.visible = false;
                })
            ;
        }

/*
        this.searchAnimation
            // scale negative
            .to([this.foregroundContainer, this.backgroundContainer], 5 / 30, {pixi: {scaleX: 1.1, scaleY: 0.8}})
            // scale back to positive
            .to(
                [this.foregroundContainer, this.backgroundContainer],
                1,
                { pixi: {scaleX: 1, scaleY: 1}, ease: Elastic.easeOut },
            )
            // wait for camera flash, if necessary
            .delay(delay)
        ;
 */
    }

    protected onMessageReceived(pTopic: string, pData: any) {
        switch (pTopic) {
            case Topics.ITEM_FOUND:
                this.OnItemFound(pData as IItemState);
                break;
            case Topics.HIDING_SPOT_SEARCHED:
                this.OnHidingSpotSearched(pData as IHidingSpotState);
                break;
        }
    }
    private OnItemFound(state: IItemState) {
        if (this.item !== undefined) {
            if (state.id === this.item.id) {
                this.interactive = true;
                // @ts-ignore
                this.hitArea = this._hitArea;
            }
        }
    }

    private SetHiddenItem(state: IHidingSpotState, levelState: ILevelState) {
        if (state.itemID !== undefined) {
            const itemconfigs = GameState.Instance.currentLevelConfig.hiddenitems as IHiddenItemConfig[];
            const itemstates = levelState.items;
            const itemConfig = itemconfigs.find((i) => { // TODO: cache this lookup
                if (typeof (i.item) === "string") {
                    return i.item === state.itemID;
                }
                else {
                    return i.item.id === state.itemID;
                }
            });
            const itemState = itemstates[state.itemID];
            if (itemConfig !== undefined && itemState !== undefined) {
                this._item = new HiddenItem(itemConfig, levelState);
                this.addChild(this._item);
            }
        }
    }

    private SetPosition(config: IHidingSpotConfig, state: IHidingSpotState) {
        const {x, y} = config.hitarea;
        // from top left of viewport to center of hiding spot
        this.position.set(x, y);
    }

    private SetHitArea(config: IHidingSpotConfig, state: IHidingSpotState, levelState: ILevelState) {
        this.buttonMode = false; // no pointer
        this.interactiveChildren = true;
        // @ts-ignore
        this._hitArea = null; // use child hit areas
        const clickThrough = true;
        this.foregroundContainer.interactiveChildren = !clickThrough;
        this.backgroundContainer.interactiveChildren = !clickThrough;

        for (const sprite of this.foregroundSprites) {
            sprite.interactive = !clickThrough;
            sprite.hitArea = new PixelPerfectHitArea(Application.instance.renderer, sprite, 0.9 * 255);
        }
        for (const sprite of this.backgroundSprites) {
            sprite.interactive = !clickThrough;
            sprite.hitArea = new PixelPerfectHitArea(Application.instance.renderer, sprite, 0.9 * 255);
        }

        if (this.item === undefined || state.itemID === undefined) {
            // @ts-ignore
            this.hitArea = this._hitArea;
        }
        else {
            const itemState: IItemState | undefined = levelState.items[state.itemID];
            if (itemState === undefined) {
                throw new Error(`Unexpected item id "${state.itemID}"`);
            }
            if (!itemState.found) {
                // @ts-ignore
                this.hitArea = this._hitArea;
                this.interactive = false; // clicks must be on the hidden item itself not on the hiding spot
            }
            else {
                // @ts-ignore
                this.hitArea = this._hitArea;
            }
        }
    }

    private OnHidingSpotSearched(state: IHidingSpotState) {
        if ( state.id !== this.id) { return; }

        this.DoSearchAnimation();
        this._searched = state.searched;
    }

    private SetSprites(levelState: ILevelState, config: IHidingSpotConfig) {

        let width = 0;
        let height = 0;

        let posX = 0;
        let posY = 0;

        if (config.foreground !== undefined) {
            if (!Array.isArray(config.foreground)) {config.foreground = [config.foreground]; }

            for (const layer of config.foreground) {
                const newSprite = makeSprite(
                    levelState.id + "/" + layer.sprite, GameConfig.spritesheets[levelState.id]);
                if (layer.halfSize) {
                    newSprite.scale.set(2);
                }
                newSprite.position.set(
                    (layer.x - this.position.x) + (newSprite.width / 2),
                    (layer.y - this.position.y) + (newSprite.height / 2),
                );
                this.foregroundContainer.addChild(newSprite);
                this.foregroundSprites.push(newSprite);

                width = Math.max(width, newSprite.width);
                height = Math.max(height, newSprite.height);
                posX = Math.min(posX, layer.x - this.position.x);
                posY = Math.min(posY, layer.y - this.position.y);
            }
        }

        if (config.background !== undefined) {
            if (!Array.isArray(config.background)) {config.background = [config.background]; }

            for (const layer of config.background) {
                const newSprite = makeSprite(
                    levelState.id + "/" + layer.sprite, GameConfig.spritesheets[levelState.id]);
                if (layer.halfSize) {
                    newSprite.scale.set(2);
                }
                newSprite.position.set(
                    (layer.x - this.position.x) + (newSprite.width / 2),
                    (layer.y - this.position.y) + (newSprite.height / 2),
                );
                this.backgroundContainer.addChild(newSprite);
                this.backgroundSprites.push(newSprite);

                width = Math.max(width, newSprite.width);
                height = Math.max(height, newSprite.height);
                posX = Math.min(posX, layer.x - this.position.x);
                posY = Math.min(posY, layer.y - this.position.y);
            }
        }

        // can't use anchor because it messes with pixelperfecthitarea
        for (const sprite of this.backgroundSprites) {
            sprite.position.x -= (width / 2) + posX;
            sprite.position.y -= height + posY;
        }
        for (const sprite of this.foregroundSprites) {
            sprite.position.x -= (width / 2) + posX;
            sprite.position.y -= height + posY;
        }

        this.backgroundContainer.position.set((width / 2) + posX, height + posY);
        this.foregroundContainer.position.set((width / 2) + posX, height + posY);
/*
        const dot = new PIXI.Graphics();
        dot.lineStyle(1, 0x000000, 1);
        dot.drawCircle(0, 0, 2.5);
        dot.moveTo(0, -5);
        dot.lineTo(0, 5);
        dot.moveTo(-5, 0);
        dot.lineTo(5, 0);
        this.foregroundContainer.addChild(dot);
        this.backgroundContainer.addChild(dot.clone());
*/
    }
}
